import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Search, ChevronDown, Copy, Lock, Sparkles, X } from "lucide-react";
import { SITE } from "@/lib/site";
import { LIBRARY_CATEGORIES, LIBRARY_ITEMS, LIBRARY_TRADES, LIBRARY_TOTAL, LIBRARY_VERSION, LIBRARY_ICONS as ICONS } from "@/lib/hs-library-sample";
import { Container, Section, Kicker, CTAButton, Reveal, usePageTitle } from "@/components/ui-kit";
import { CTABand } from "@/components/sections/Social";
import { cn } from "@/lib/utils";
import SEO from "@/lib/seo-static.json";

const catLabel = (key) => LIBRARY_CATEGORIES.find((c) => c.key === key)?.label.replace(/s$/, "") || key;
const NOUN = { training: "course", rams: "RAMS pack", risk: "risk assessment", talks: "toolbox talk", coshh: "COSHH assessment", checklists: "checklist", permits: "permit", policies: "policy", fire: "document" };

const Hero = () => (
  <section className="relative overflow-hidden pt-32 pb-12 sm:pt-40 grid-bg">
    <Container>
      <Kicker>H&S Template Library · preview</Kicker>
      <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mk-text text-balance max-w-3xl" data-testid="library-title">Browse the library before you start.</h1>
      <p className="mt-6 text-lg text-mk-dim max-w-2xl leading-relaxed">A sample of what Fixlr's H&S team maintains — {LIBRARY_TOTAL} templates across {LIBRARY_CATEGORIES.length - 1} categories, UK-law-accurate and versioned. Every one of them is yours to copy, personalise and save from day one of a trial.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <CTAButton href={SITE.signupUrl} testId="library-trial-btn">Start free trial — library included <ArrowRight className="h-4 w-4" /></CTAButton>
        <CTAButton to="/product/hs-library" variant="ghost" testId="library-module-link">About the add-on</CTAButton>
      </div>
      <dl className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl" data-testid="library-stats">
        {[[`${LIBRARY_TOTAL}`, "templates in the library"], ["9", "categories"], [LIBRARY_VERSION, "current library version"], ["Weekly", "new & updated items"]].map(([v, l]) => (
          <div key={l} className="rounded-xl border border-mk-border bg-mk-surface/60 p-4"><dt className="font-display text-2xl font-bold text-mk-lime whitespace-nowrap">{v}</dt><dd className="mt-1 text-xs text-mk-muted">{l}</dd></div>
        ))}
      </dl>
    </Container>
  </section>
);

const CopyGate = ({ item, onClose }) => (
  <AnimatePresence>
    {item && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="library-copy-gate">
        <motion.div initial={{ y: 16, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 16, scale: 0.98 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-mk-border bg-mk-surface p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-mk-lime/40 bg-mk-lime/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-mk-lime"><Lock className="h-3 w-3" />Copying needs a workspace</div>
            <button type="button" onClick={onClose} className="text-mk-muted hover:text-mk-text" aria-label="Close" data-testid="library-copy-gate-close"><X className="h-4 w-4" /></button>
          </div>
          <h3 className="mt-4 font-display text-xl font-bold text-mk-text">{item.title}</h3>
          <p className="mt-2 text-sm text-mk-dim">Templates copy into <em>your</em> workspace as a draft to personalise. Start a free trial — the H&S Template Library is included — and this {NOUN[item.cat]} is one click away.</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <a href={SITE.signupUrl} target="_blank" rel="noreferrer" className="btn-lime flex-1 justify-center" data-testid="library-copy-gate-trial">Start free trial <ArrowRight className="h-4 w-4" /></a>
            <a href={SITE.loginUrl} target="_blank" rel="noreferrer" className="btn-ghost flex-1 justify-center" data-testid="library-copy-gate-signin">I have a workspace — sign in</a>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const ItemCard = ({ item, onCopy }) => {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[item.cat];
  return (
    <div className="card-dark p-4 flex flex-col" data-testid={`library-item-${item.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-mk-muted"><Icon className="h-3.5 w-3.5 text-mk-lime" />{catLabel(item.cat)}</span>
        {item.badge && <span className={cn("rounded-full px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider", item.badge === "New" ? "bg-mk-lime/15 text-mk-lime" : "bg-amber-400/15 text-amber-300")}>{item.badge}</span>}
      </div>
      <h3 className="mt-2 text-sm font-semibold text-mk-text leading-snug">{item.title}</h3>
      <div className="mt-1.5 text-[11px] text-mk-muted">{item.trade} · {item.version}{item.mins ? ` · ${item.mins} min` : ""} · copied {item.copies.toLocaleString()}×</div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen((s) => !s)} className="inline-flex items-center gap-1 text-xs text-mk-dim hover:text-mk-text transition-colors" data-testid={`library-item-outline-${item.id}`}>What's inside <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} /></button>
        <button type="button" onClick={() => onCopy(item)} className="inline-flex items-center gap-1 text-xs font-semibold text-mk-lime hover:text-mk-text transition-colors" data-testid={`library-item-copy-${item.id}`}><Copy className="h-3.5 w-3.5" />Copy to workspace</button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.ol initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mt-3 space-y-1 border-t border-mk-border/60 pt-3 text-xs text-mk-dim" data-testid={`library-item-outline-list-${item.id}`}>
            {item.outline.map((s, i) => <li key={s} className="flex gap-2"><span className="font-mono text-[10px] text-mk-muted">0{i + 1}</span>{s}</li>)}
          </motion.ol>
        )}
      </AnimatePresence>
    </div>
  );
};

const Browser = () => {
  const [cat, setCat] = useState("all");
  const [trade, setTrade] = useState("All trades");
  const [q, setQ] = useState("");
  const [gate, setGate] = useState(null);
  const items = useMemo(() => LIBRARY_ITEMS.filter((i) => (cat === "all" || i.cat === cat) && (trade === "All trades" || i.trade === trade || (trade === "General" && i.trade === "General")) && (!q || i.title.toLowerCase().includes(q.toLowerCase()))), [cat, trade, q]);
  const active = LIBRARY_CATEGORIES.find((c) => c.key === cat);
  return (
    <Section className="pt-6" data-testid="library-browser">
      <Container>
        <div className="sticky top-[72px] z-30 -mx-5 sm:-mx-8 lg:mx-0 px-5 sm:px-8 lg:px-0 py-3 glass border-y lg:border lg:rounded-2xl border-mk-border/70">
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="library-tabs">
            {LIBRARY_CATEGORIES.map((c) => (
              <button key={c.key} type="button" onClick={() => setCat(c.key)} data-testid={`library-tab-${c.key}`} aria-pressed={cat === c.key} className={cn("shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors", cat === c.key ? "border-mk-lime bg-mk-lime text-mk-bg" : "border-mk-border text-mk-dim hover:text-mk-text hover:border-mk-strong")}>
                {c.label}<span className={cn("font-mono text-[10px]", cat === c.key ? "text-mk-bg/70" : "text-mk-muted")}>{c.count}</span>
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <label className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mk-muted" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles — asbestos, ladder, COSHH, permit…" className="input-dark !pl-9 !py-2.5" data-testid="library-search" /></label>
            <select value={trade} onChange={(e) => setTrade(e.target.value)} className="input-dark !py-2.5 sm:!w-auto" data-testid="library-trade-filter">{LIBRARY_TRADES.map((t) => <option key={t}>{t}</option>)}</select>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-mk-text" data-testid="library-heading">{active.key === "all" ? "A sample of the library" : active.label}</h2>
            {active.blurb && <p className="mt-1 text-sm text-mk-dim max-w-2xl">{active.blurb}</p>}
          </div>
          <div className="text-xs text-mk-muted" data-testid="library-count">Showing {items.length} of {active.key === "all" ? LIBRARY_ITEMS.length : active.count} {active.key === "all" ? "sampled titles" : "in the live library"}</div>
        </div>

        {items.length ? (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
            {items.map((item) => <ItemCard key={item.id} item={item} onCopy={setGate} />)}
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-dashed border-mk-border p-10 text-center text-sm text-mk-dim" data-testid="library-empty">Nothing in this sample matches "{q}". The live library has {LIBRARY_TOTAL} titles — and if yours isn't there, write it once and propose it.</div>
        )}

        <Reveal className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-mk-lime/30 bg-mk-lime/[0.04] p-6">
          <div className="min-w-0"><div className="eyebrow inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />This is a sample</div><p className="mt-2 text-sm text-mk-dim">Titles shown here mirror the live library at {LIBRARY_VERSION}; the full set — with content, versions and your own copies — lives in your workspace. New and updated items land weekly, and anything you write can be proposed back.</p></div>
          <Link to="/product/hs-library" className="btn-ghost shrink-0" data-testid="library-howitworks-link">How copying & sharing works <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
      </Container>
      <CopyGate item={gate} onClose={() => setGate(null)} />
    </Section>
  );
};

export default function Library() {
  usePageTitle(SEO["/library"].title, SEO["/library"].description, SEO["/library"].image);
  return (
    <div data-testid="library-page">
      <Hero />
      <Browser />
      <CTABand />
    </div>
  );
}
